import { useId, type ReactNode } from 'react';
import { formatNumber } from '../../format/format.ts';
import styles from './Tabs.module.css';

export type Tab = { id: string; label: string; content: ReactNode; count?: number | undefined };

export type TabsProps = {
  tabs: readonly Tab[];
  activeId: string;
  onChange: (id: string) => void;
  framed?: boolean;
  locale?: string;
};

export const Tabs = ({ tabs, activeId, onChange, framed = false, locale = 'en' }: TabsProps) => {
  const baseId = useId();
  const active = tabs.find((tab) => tab.id === activeId) ?? tabs[0];
  return (
    <div className={framed ? styles['framed'] : undefined}>
      <div role="tablist" className={styles['list']}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`${baseId}-${tab.id}`}
            aria-selected={tab.id === active?.id}
            aria-controls={`${baseId}-${tab.id}-panel`}
            className={[styles['tab'], tab.id === active?.id ? styles['active'] : ''].join(' ')}
            onClick={() => {
              onChange(tab.id);
            }}
          >
            {tab.label}
            {tab.count === undefined ? null : (
              <span className={styles['count']}>{formatNumber(tab.count, locale)}</span>
            )}
          </button>
        ))}
      </div>
      {active ? (
        <div
          role="tabpanel"
          id={`${baseId}-${active.id}-panel`}
          aria-labelledby={`${baseId}-${active.id}`}
          className={styles['panel']}
        >
          {active.content}
        </div>
      ) : null}
    </div>
  );
};
